import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useRazorpay from "react-razorpay";
import Header from "../components/Header";
import Footer from "../components/Footer";
import InteraktScript from "../../common/InteraktScript";
import configData from "../../config.json";
import "./../home/home.css";
import "./TrackerUser.css";

const IOS_APP_URL = "https://apps.apple.com/us/app/myprega/id6778800135";
const ANDROID_APP_URL = "https://play.google.com/store/apps/details?id=com.myprega.app";

function TrackerUser() {
  const [userid, setUserid] = useState(0);
  const [patientData, setPatientData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false);
  const [paymentSuccessMessage, setPaymentSuccessMessage] = useState("");
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState("");
  const careRowRefs = useRef([]);
  const careAutoIndexRef = useRef([]);
  const [Razorpay] = useRazorpay();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const userIdValue = localStorage.getItem("userId");
    setUserid(userIdValue);
  }, []);

  useEffect(() => {
    if (!window.matchMedia("(max-width: 767px)").matches) return undefined;

    const cleanups = careRowRefs.current.filter(Boolean).map((row, rowIndex) => {
      let intervalId;
      let autoScrollStopped = false;

      const stopAutoScroll = () => {
        if (autoScrollStopped) return;
        autoScrollStopped = true;
        window.clearInterval(intervalId);
      };

      const scrollToNextCardGroup = () => {
        const cards = Array.from(row.querySelectorAll(".tracker-care-card"));
        if (autoScrollStopped || cards.length < 2) return;

        const rowPadding = Number.parseFloat(window.getComputedStyle(row).paddingLeft) || 0;
        const slidePositions = [...new Set(cards.map((card) => card.offsetLeft - rowPadding))];
        const nextIndex = ((careAutoIndexRef.current[rowIndex] || 0) + 1) % slidePositions.length;
        careAutoIndexRef.current[rowIndex] = nextIndex;

        row.scrollTo({
          left: slidePositions[nextIndex],
          behavior: "smooth",
        });
      };

      row.addEventListener("pointerdown", stopAutoScroll, { passive: true });
      row.addEventListener("touchstart", stopAutoScroll, { passive: true });
      row.addEventListener("wheel", stopAutoScroll, { passive: true });
      row.addEventListener("keydown", stopAutoScroll);
      intervalId = window.setInterval(scrollToNextCardGroup, 2000);

      return () => {
        window.clearInterval(intervalId);
        row.removeEventListener("pointerdown", stopAutoScroll);
        row.removeEventListener("touchstart", stopAutoScroll);
        row.removeEventListener("wheel", stopAutoScroll);
        row.removeEventListener("keydown", stopAutoScroll);
      };
    });

    return () => cleanups.forEach((cleanup) => cleanup());
  }, []);

  const isLoggedIn = () => {
    return userid && userid !== "0" && userid !== "null";
  };

  const getPatientDetails = async (userIdValue) => {
    const response = await axios.post(
      `${configData.SERVER_API_URL}/users/get_single_user`,
      { userId: userIdValue }
    );

    if (!response.data?.success) {
      throw new Error("Unable to load user details.");
    }

    setPatientData(response.data.user);
    return response.data.user;
  };

  const loadPlans = async () => {
    setIsLoadingPlans(true);
    setPaymentMessage("");

    try {
      const response = await axios.get(
        `${configData.SERVER_API_URL}/appSubscription/get_plans`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success && Array.isArray(response.data.plans)) {
        setPlans(response.data.plans);
        setSelectedPlanId(response.data.plans[0]?.id?.toString() || "");
      } else {
        setPaymentMessage("Plans are not available right now. Please try again.");
      }
    } catch (error) {
      console.error("Error loading subscription plans:", error);
      setPaymentMessage("Unable to load plans. Please try again.");
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handleBuyNow = async () => {
    if (!isLoggedIn()) {
      alert("Please login to buy.");
      navigate("/patient/loginform");
      return;
    }

    setShowPlanModal(true);
    await loadPlans();
  };

  const handlePayment = async () => {
    const selectedPlan = plans.find(
      (plan) => plan.id?.toString() === selectedPlanId
    );

    if (!selectedPlan) {
      setPaymentMessage("Please select a plan to continue.");
      return;
    }

    setIsProcessingPayment(true);
    setPaymentMessage("");

    try {
      const user = patientData || (await getPatientDetails(userid));
      const orderResponse = await axios.post(
        `${configData.SERVER_API_URL}/appSubscription/create_order`,
        {
          user_id: Number(userid),
          plan_id: selectedPlan.id,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const order = orderResponse.data;

      if (!order?.success || !order?.order_id) {
        setPaymentMessage("Unable to create payment order. Please try again.");
        return;
      }

      const options = {
        key: "rzp_live_5FkwM0dSiSrFis",
        amount: Number(order.amount) * 100,
        currency: order.currency || "INR",
        name: "Myprega",
        description: selectedPlan.name,
        image: "https://myprega.com/logo.png",
        order_id: order.order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await axios.post(
              `${configData.SERVER_API_URL}/appSubscription/verify_payment`,
              {
                user_id: Number(userid),
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            if (verifyResponse.data?.success) {
              localStorage.setItem("isPaidUser", "true");
              setShowPlanModal(false);
              setPaymentSuccessMessage(
                verifyResponse.data.message ||
                  "Your subscription has been activated successfully."
              );
              setShowAppDownloadModal(true);
            } else {
              setPaymentMessage(
                verifyResponse.data?.message ||
                  "Payment verification failed. Please contact support."
              );
            }
          } catch (error) {
            console.error("Error verifying payment:", error);
            setPaymentMessage(
              "Payment completed, but verification failed. Please contact support."
            );
          } finally {
            setIsProcessingPayment(false);
          }
        },
        prefill: {
          name:
            user?.fullName ||
            user?.full_name ||
            `${user?.fname || ""} ${user?.lname || ""}`.trim(),
          email: user?.email || "",
          contact: user?.phone || user?.number || "",
        },
        notes: {
          plan_id: selectedPlan.id,
          plan_name: selectedPlan.name,
        },
        theme: {
          color: "#F0588B",
        },
        modal: {
          ondismiss: () => {
            setIsProcessingPayment(false);
          },
        },
      };

      const razorpayInstance = new Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      console.error("Error starting payment:", error);
      setPaymentMessage("Unable to start payment. Please try again.");
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="cherish-page tracker-page">
      <Header userid={userid} />
      <main className="tracker-content">
        <section className="tracker-hero">
          <div className="tracker-hero__copy">
            <h1><span className="tracker-hero__nowrap">Your Pregnancy Happens</span> <span>Every Day.</span> Your <span>Care</span> Should Too.</h1>
            <p className="tracker-hero__lead">India’s first pregnancy care ecosystem built for both parents-to-be.</p>
            <p>You visit your gynaecologist once a month, but what about the 29 days in between? When every new symptom brings questions, every small doubt leaves you wondering whether it’s normal or needs attention, and Googling or asking AI only adds to the confusion.</p>
            <p>That’s where MyPrega’s Daily Handholding Care comes in — a connected pregnancy care ecosystem designed for both parents-to-be, bringing personalised daily support, expert guidance, health tracking, emotional wellbeing, physical fitness training and everything you need between your gynaecologist visits together in one place.</p>
            <p>Navigate pregnancy with complete peace of mind, knowing you have personalized guidance every day to enjoy this journey together rather than figuring it all out alone.</p>
            <button type="button" className="btn btn-cherish-lg tracker-user-cta" onClick={handleBuyNow}>START YOUR DAILY HANDHOLDING JOURNEY</button>
          </div>
          <div className="tracker-hero__art">
            <picture>
              <source media="(max-width: 767px)" srcSet="/tracker-daily-care-mobile.jpeg" />
              <img src="/tracker-daily-care-hero.jpeg" alt="Expecting parents supported by MyPrega daily care" />
            </picture>
          </div>
        </section>

        <section className="tracker-promise">
          <p>Add an extra layer of safety to your pregnancy through continuous support and expert access.</p>
          <strong><span>One pregnancy.</span> One connected care ecosystem. <span>One place for everything you need between consultations.</span></strong>
        </section>

        <section className="tracker-care-section">
          <header className="tracker-section-heading"><div><h2>Mom Care — Complete Maternal &amp; Daily <span>Health Check-In</span></h2></div><img src="/Packaged1.png" alt="Mom using MyPrega daily care" /></header>
          <div
            className="tracker-care-grid"
            ref={(node) => { careRowRefs.current[0] = node; }}
            tabIndex="0"
            aria-label="Mom Care features"
          >
            {[
              ["01", "Personalised Nutrition", "Get weekly one-on-one video consultations with a pregnancy coach for personalised nutrition guidance based on your changing nutritional needs, eating patterns, and pregnancy-related concerns.", "bi-egg-fried"],
              ["02", "Personalised Emotional Wellness", "Get weekly one-on-one video consultations with a pregnancy coach for emotional wellness support — helping you understand your emotions, manage anxiety and stress, and develop practical ways to navigate them.", "bi-heart"],
              ["03", "Personalised Movement Plan", "A technology-based, expert-guided movement plan personalised to your trimester, body requirements, medical history, energy levels, and preferences — designed to build mobility, flexibility, and strength while preparing your body for birth and recovery.", "bi-activity"],
              ["04", "Advanced Health Tracker", "Track nine key health parameters regularly through MyPrega’s technology-based Health Tracker. Your data is tracked and analysed to help identify potential risk factors early, before they may develop into complications, with access to medical expert guidance when needed.", "bi-graph-up-arrow"],
              ["05", "Nurture AI", "A specialised 24/7 AI companion built to answer your pregnancy-related worries, doubts, and questions whenever you need support.", "bi-robot"],
              ["06", "Breathing Techniques", "Learn expert-guided, pregnancy-appropriate breathing and relaxation techniques to regulate your breathing, manage stress and discomfort, and build awareness of your breath as you prepare for birth.", "bi-wind"],
              ["07", "Pain Management Guidance", "Understand pregnancy-related pain and discomfort, recognise what may require attention, and learn practical coping strategies through specially designed modules and expert guidance.", "bi-bandaid"],
              ["08", "Garbhasanskar Activities", "Daily mindful practices designed to help you feel emotionally calm and deeply connected with your baby, through meaningful bonding and relaxation activities.", "bi-flower1"],
              ["09", "24×7 Pregnancy Expert Access", "Get 24×7 access to pregnancy experts for pregnancy-related questions between consultations — so everyday doubts, symptoms, and concerns have a trusted place to go instead of leaving you searching and anxious.", "bi-headset"],
              ["10", "Pregnancy Library", "Access doctor-backed pregnancy blogs to understand your changing body, your baby’s development, and what to expect throughout your pregnancy — helping you feel more informed and prepared.", "bi-book"],
              ["11", "Mom-to-Be Community", "A safe and supportive space for moms-to-be to share, learn, and connect with others walking the same pregnancy journey.", "bi-people"],
            ].map(([id, title, desc, icon]) => <article className="tracker-care-card" key={id}><span aria-hidden="true"><i className={`bi ${icon}`} /></span><h3>{title}</h3><p>{desc}</p></article>)}
          </div>
        </section>

        <section className="tracker-dad-section">
          <header className="tracker-section-heading tracker-section-heading--reverse"><div><h2>Dad-to-Be Care — Active Partnership &amp; <span>Co-Parenting</span></h2></div><img src="/dad-to-be-care.jpeg" alt="Expecting parents supported by their connected care team" /></header>
          <div
            className="tracker-care-grid tracker-care-grid--dad"
            ref={(node) => { careRowRefs.current[1] = node; }}
            tabIndex="0"
            aria-label="Dad-to-Be Care features"
          >
            {[
              ["01", "24×7 Medical Expert Access", "Get 24×7 access to pregnancy experts for pregnancy-related questions and guidance on how to support your partner through her changing needs and concerns.", "bi-chat-heart"],
              ["02", "Dad-to-Be Community", "A private, judgement-free space to connect with other expecting fathers, share experiences, and navigate the unique challenges of pregnancy together.", "bi-people"],
              ["03", "Understand Her Pregnancy", "Curated modules help you understand the physical and emotional changes your partner may experience — and what she may need from you at every stage.", "bi-journal-medical"],
              ["04", "Daily Bonding Tips", "Simple, meaningful activities to help you connect with your baby and become an active part of the pregnancy journey before birth.", "bi-heart"],
              ["05", "Daily Affirmations", "Gentle daily prompts to encourage positive communication, emotional reassurance, and a calmer home environment.", "bi-chat-quote"],
              ["06", "Make Her Feel Loved", "Practical ideas for small gestures, words, and actions that can reduce her stress and help her feel cared for throughout pregnancy.", "bi-gift"],
              ["07", "Shared Partnership Modules", "Expert-guided modules that help couples understand, communicate, and navigate pregnancy together — rather than as two separate journeys.", "bi-people-fill"],
            ].map(([id, title, desc, icon]) => <article className="tracker-care-card" key={id}><span aria-hidden="true"><i className={`bi ${icon}`} /></span><h3>{title}</h3><p>{desc}</p></article>)}
          </div>
        </section>

        <section className="tracker-compare">
          <h2>Beyond Your Gynaecologist: The Support You Need <span>Between Consultations</span></h2>
          <div className="tracker-compare__visuals"><div><strong>Without MyPrega</strong><img src="/vs1.png" alt="Pregnancy care without MyPrega" /></div><div><strong>With MyPrega Daily Handholding</strong><img src="/vs2.png" alt="Pregnancy care with MyPrega" /></div></div>
          <div className="tracker-compare__table"><div className="tracker-compare__head"><strong>Without MyPrega</strong><strong>With MyPrega Daily Handholding</strong></div>
            {[
              ["Generic diet advice & myths", "Personalised 1-on-1 nutrition guidance"],
              ["Figuring symptoms out alone", "Dedicated pregnancy coach + 24×7 medical expert access"],
              ["silent Anxiety and emotional overwhelm", "Personalised emotional wellness support"],
              ["Generic, rigid exercise routines", "Personalised, trimester-specific movement plan"],
              ["Scattered information and conflicting advice", "One connected, doctor-backed ecosystem"],
              ["Mom navigating pregnancy alone", "Active partner care & dad involvement"],
              ["Uncertainty and late night guessing", "Daily health tracking + smart early risk analysis"],
            ].map(([before, after]) => <div className="tracker-compare__row" key={before}><span>{before}</span><span>{after}</span></div>)}
          </div>
        </section>

        <section className="tracker-closing">
          <p>For less than the cost of a coffee date, give yourself and your partner access to a connected pregnancy care system designed to help you feel more informed, supported and confident throughout your journey.</p>
          <p>Because peace of mind, feeling prepared and experiencing pregnancy together are worth investing in.</p>
          <h2>Don’t Navigate Pregnancy Alone. Make Every Day Count.</h2>
          <button type="button" className="btn btn-cherish-lg tracker-user-cta" onClick={handleBuyNow}>START YOUR DAILY HANDHOLDING JOURNEY</button>
        </section>
      </main>

      {showPlanModal && (
        <div className="subscription-modal-backdrop" role="dialog" aria-modal="true">
          <div className="subscription-modal">
            <button
              type="button"
              className="subscription-modal__close"
              onClick={() => setShowPlanModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <h3>Choose Your Plan</h3>
            <p>Select a subscription duration to continue with Myprega care.</p>

            {isLoadingPlans ? (
              <div className="subscription-modal__status">Loading plans...</div>
            ) : (
              <div className="subscription-plan-list">
                {plans.map((plan) => (
                  <label
                    className={`subscription-plan-card ${
                      selectedPlanId === plan.id?.toString()
                        ? "subscription-plan-card--active"
                        : ""
                    }`}
                    key={plan.id}
                  >
                    <input
                      type="radio"
                      name="subscription-plan"
                      value={plan.id}
                      checked={selectedPlanId === plan.id?.toString()}
                      onChange={(event) => setSelectedPlanId(event.target.value)}
                    />
                    <span>
                      <strong>{plan.name}</strong>
                      <small>{plan.duration_months} months</small>
                      {plan.description && <em>{plan.description}</em>}
                    </span>
                    <b>Rs. {plan.price}</b>
                  </label>
                ))}
              </div>
            )}

            {paymentMessage && (
              <div className="subscription-modal__message">{paymentMessage}</div>
            )}

            <button
              type="button"
              className="btn btn-cherish-lg tracker-user-cta subscription-modal__pay"
              onClick={handlePayment}
              disabled={isLoadingPlans || isProcessingPayment || plans.length === 0}
            >
              {isProcessingPayment ? "Processing..." : "Proceed to Pay"}
            </button>
          </div>
        </div>
      )}

      {showAppDownloadModal && (
        <div className="app-download-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="app-download-title">
          <div className="app-download-modal">
            <button
              type="button"
              className="app-download-modal__close"
              onClick={() => setShowAppDownloadModal(false)}
              aria-label="Close app download popup"
            >
              &times;
            </button>

            <div className="app-download-modal__success" aria-hidden="true">
              <i className="bi bi-check-lg" />
            </div>
            <p className="app-download-modal__eyebrow">Payment successful</p>
            <h2 id="app-download-title">Continue Your Care in the MyPrega App</h2>
            <p className="app-download-modal__message">{paymentSuccessMessage}</p>
            <p className="app-download-modal__intro">
              All your Daily Handholding features are available in our app. Download MyPrega to continue your care journey and access your support every day.
            </p>

            <div className="app-download-options">
              <article className="app-download-option">
                <div className="app-download-option__heading">
                  <i className="bi bi-apple" aria-hidden="true" />
                  <h3>Download for iPhone</h3>
                </div>
                <img src="/myprega-ios-qr.png" alt="QR code to download MyPrega from the Apple App Store" />
                <a href={IOS_APP_URL} target="_blank" rel="noreferrer" className="app-download-option__button app-download-option__button--ios">
                  <i className="bi bi-apple" aria-hidden="true" />
                  Download on the App Store
                </a>
              </article>

              <article className="app-download-option">
                <div className="app-download-option__heading">
                  <i className="bi bi-google-play" aria-hidden="true" />
                  <h3>Download for Android</h3>
                </div>
                <img src="/myprega-android-qr.png" alt="QR code to download MyPrega from Google Play" />
                <a href={ANDROID_APP_URL} target="_blank" rel="noreferrer" className="app-download-option__button app-download-option__button--android">
                  <i className="bi bi-google-play" aria-hidden="true" />
                  Get it on Google Play
                </a>
              </article>
            </div>

            <p className="app-download-modal__scan-note">Scan the QR code with your phone camera or use the download button.</p>
          </div>
        </div>
      )}

      <Footer />
      <InteraktScript />
    </div>
  );
}

export default TrackerUser;
